import {
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    IconButton,
    Link,
    Radio,
    RadioGroup,
    Stack,
    Typography
} from "@mui/material";
import { colours } from "../../../StyleConstants";
import HighlightOffSharpIcon from "@mui/icons-material/HighlightOffSharp";
import LassoModal from "../../../components/LassoModal";
import React, { useEffect, useState } from "react";
import { useRevokeUserMutation } from "../../../api/userApiSlice";
import { setNewNotification } from "../../common/notificationSlice";
import { useDispatch } from "react-redux";
import { revocationReasons } from "../revocationReasons";

interface modalProps {
    open: boolean,
    setOpen: Function,
    username: string
    email: string
}

const UserActionConfirm = (props: modalProps) => {
    const [revoke] = useRevokeUserMutation({});
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false)
    const [revokeReason, setRevokeReason] = useState("")
    const [reasonError, setReasonError] = useState(false)

    // Reset local state whenever a new user is selected or the modal is reopened
    useEffect(() => {
        if (props.open) {
            setRevokeReason("")
            setReasonError(false)
            setLoading(false)
        }
    }, [props.open, props.username]);

    const handleClickCancel = () => {
        props.setOpen(false)
        setRevokeReason("")
        setReasonError(false)
    }

    const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRevokeReason(event.target.value)
        setReasonError(false)
    }

    const handleClickRevoke = () => {
        // LC-2126: a reason is mandatory before access can be revoked
        if (!revokeReason) {
            setReasonError(true)
            return
        }

        setLoading(true)
        revoke({ username: props.username, revokeReason: revokeReason }).then((response) => {
            if ("error" in response){
                dispatch(setNewNotification({
                    message: "Unable to revoke access. Please try again later.",
                    type: 'error'
                }))
            }
            else if ("data" in response){
                dispatch(setNewNotification({
                    message: "DUC access for " + response?.data?.fullName + " has been successfully revoked.",
                    type: 'success'
                }))
            }
            else{
                dispatch(setNewNotification({
                    message: "Unable to revoke access. Please try again later.",
                    type: 'error'
                }))
            }
            setLoading(false)
            setRevokeReason("")
            setReasonError(false)
            props.setOpen(false)
        })

    }

    return (
        <LassoModal open={props.open}>
            {!loading && <Stack gap={4} padding={3} sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <Stack flexDirection="row" justifyContent="space-between">
                    <Typography variant="h5" fontWeight="600" color={colours.midnightBlue.blue} textAlign="center">
                        Revoke Access
                    </Typography>
                    <IconButton aria-label="Close" onClick={handleClickCancel}>
                        <HighlightOffSharpIcon/>
                    </IconButton>
                </Stack>
                <Stack gap={1}>
                    <Typography color={colours.midnightBlue.blue} textAlign="center">
                        Select a reason for revoking access and confirm for the following user:
                    </Typography>
                    <Typography color={colours.midnightBlue.blue} textAlign="center" fontWeight="600">
                        {props.username}
                    </Typography>

                </Stack>

                <FormControl component="fieldset" error={reasonError}>
                    <RadioGroup
                        aria-label="Reason for revoking access"
                        name="revocation-reason"
                        value={revokeReason}
                        onChange={handleReasonChange}
                    >
                        {revocationReasons.map((reason, index) => (
                            <FormControlLabel
                                key={reason}
                                value={reason}
                                control={<Radio />}
                                label={index === 0 ? (
                                    <Typography>
                                        Requestor&apos;s institution is located in a country of concern. See NIH Notice: <Link
                                            href="https://grants.nih.gov/grants/guide/notice-files/NOT-OD-25-083.html"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            NOT-OD-25-083: Implementation Update: Enhancing Security Measures for NIH Controlled-Access Data
                                        </Link>
                                    </Typography>
                                ) : reason}
                                sx={{ marginBottom: 1 }}
                            />
                        ))}
                    </RadioGroup>
                    {reasonError && (
                        <Typography color="error" fontSize="0.75rem" sx={{ marginTop: 1 }}>
                            Please select a reason for revoking access
                        </Typography>
                    )}
                </FormControl>

                <Stack flexDirection="column" justifyContent="center" alignItems="center" gap={2}>
                    <Button variant="primary" onClick={handleClickRevoke} disabled={!revokeReason}>
                        Confirm
                    </Button>
                    <Button size="small" variant="secondary" onClick={handleClickCancel} >
                        Cancel
                    </Button>
                </Stack>
            </Stack>}

            {loading &&
            <CircularProgress/>}

        </LassoModal>
    )
}
export default UserActionConfirm
